import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgForm } from '@angular/forms';
import { BoardSeat, CompanyBasic, CreateBoardSeat, Person } from '../../models';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CompanyService } from '../../../core/data-services/company';
import { BoardSeatRepresenting, BoardSeatRepresentingLabels } from '../../enums/board-seat-representing.enum';
import { RestrictionsIntegrationService } from '../../../modules/restrictions/restrictions-integration.service';
import { environment } from '../../../../environments/environment';
import { ModalSuggestion } from '../../models/restrictions/modal-suggestion.model';
import { DateTime } from 'luxon';
import { RMS_SHEET_MODAL_CLASS } from '../../constants/sheet-modal';

@Component({
    selector: 'rms-board-seat-add-edit',
    templateUrl: './board-seat-add-edit.component.html',
    styleUrls: ['./board-seat-add-edit.component.scss'],
    standalone: false
})
export class BoardSeatAddEditComponent implements OnInit {

  @ViewChild('boardSeatAddEditModal', { static: true }) modal: ElementRef;
  private modalRef: NgbModalRef;

  public boardSeat: BoardSeat = new BoardSeat();

  public companyTypeahead = new Subject<string>();
  public companies: Array<any>;
  public companySearching = false;
  public companyList: Array<CompanyBasic> = new Array<CompanyBasic>();

  public people: Array<Person> = new Array<Person>();

  public startDate: DateTime;
  public endDate: DateTime;
  public maxEndDate = DateTime.now().toLocal();
  public resignationSentDate: DateTime;

  public editMode = false;

  public forceEzeError = false;

  public readonly environment = environment;
  public readonly boardSeatRepresentingTypes = Object.values(BoardSeatRepresenting);
  public readonly BoardSeatRepresentingLabels = BoardSeatRepresentingLabels;

  constructor(
    private modalService: NgbModal,
    private companyService: CompanyService,
    private cd: ChangeDetectorRef,
    public restrictionsIntegrationService: RestrictionsIntegrationService,
  ) { }

  ngOnInit() {
    this.companyAutocomplete();
    this.restrictionsIntegrationService.people.subscribe(resp => {
      this.people = resp;
    });
  }

  public openModal(boardSeat?: BoardSeat, suggestedModalData?: ModalSuggestion) {
    if (boardSeat) {
      this.boardSeat = new BoardSeat(boardSeat);
      this.startDate = this.boardSeat.startDate;
      this.endDate = this.boardSeat.endDate || (this.editMode && !this.boardSeat.active ? DateTime.now().toLocal() : null);
      this.resignationSentDate = this.boardSeat.resignationSentDate;
      this.editMode = true;
    } else {
      this.boardSeat = new BoardSeat();
      this.startDate = DateTime.now();
      this.endDate = this.editMode && !this.boardSeat.active ? DateTime.now().toLocal() : null;
      this.resignationSentDate = null;
      this.boardSeat.company = suggestedModalData.company;
      this.boardSeat.person = suggestedModalData.person;
      this.boardSeat.representing = BoardSeatRepresenting.RA_CAP;
    }
    this.modalRef = this.modalService.open(this.modal, { size: 'lg', centered: true, fullscreen: 'sm', windowClass: RMS_SHEET_MODAL_CLASS });
    return this.modalRef;
  }

  public submit(form: NgForm) {
    if (form.invalid) {
      return;
    }
    this.boardSeat.startDate = this.startDate;
    this.boardSeat.endDate = this.endDate;
    this.boardSeat.resignationSentDate = this.resignationSentDate;
    const boardSeat = new CreateBoardSeat(this.boardSeat);
    boardSeat['forceEzeError'] = this.forceEzeError;
    this.modalRef.close(boardSeat);
  }

  private companyAutocomplete() {
    this.companyTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => {
        this.companySearching = true;
        return this.companyService.searchCompanies({ q: term });
      }),
    ).subscribe(value => {
      this.companySearching = false;
      this.companyList = value.records;
      this.cd.markForCheck();
    }, () => {
      this.companySearching = false;
      this.companyList = new Array<CompanyBasic>();
    });
  }
}
